import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { FileText, ArrowLeft } from 'lucide-react';
import useCanonical from '../../hooks/useCanonical';

function TermsPage() {
  // Set canonical URL
  useCanonical('https://servio.legal/terms');
  
  return (
    <div className="min-h-screen flex flex-col noise-overlay">
      <Helmet>
        <title>Terms of Service | Servio</title>
        <meta name="description" content="Read the Terms of Service for Servio, the modern process server directory. Understand your rights and responsibilities when using our platform." />
        <link rel="canonical" href="https://servio.legal/terms" />
      </Helmet>
      <Navbar />
      
      <main className="flex-1 py-12 lg:py-16">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-teal-400 transition-colors mb-4">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-teal-500/10 border border-teal-500/20">
                  <FileText className="h-6 w-6 text-teal-400" />
                </div>
                <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
              </div>
              <p className="text-slate-400">Last updated: February 2026</p>
            </div>

            {/* Content */}
            <div className="prose prose-invert prose-slate max-w-none space-y-8">
              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">1. Acceptance of Terms</h2>
                <p className="text-slate-400 leading-relaxed">
                  By accessing or using Servio ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service. Servio reserves the right to modify these terms at any time, and your continued use of the Service constitutes acceptance of any changes.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">2. Description of Service</h2>
                <p className="text-slate-400 leading-relaxed">
                  Servio is an online directory platform that connects process servers with clients seeking legal document service. The Service provides:
                </p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li>Profile listings for registered process servers</li>
                  <li>Search functionality for clients to find process servers</li>
                  <li>Quote request and communication tools</li>
                  <li>Verification badge systems for professional credentials</li>
                </ul>
                <p className="text-slate-400 leading-relaxed mt-4">
                  Servio is a directory and communication platform only. We do not employ process servers, guarantee the quality of service, or assume responsibility for any services performed by listed professionals.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">3. User Accounts</h2>
                <h3 className="text-lg font-medium text-white mt-4 mb-2">3.1 Registration</h3>
                <p className="text-slate-400 leading-relaxed">
                  To use certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during registration and to keep this information updated.
                </p>
                <h3 className="text-lg font-medium text-white mt-4 mb-2">3.2 Account Security</h3>
                <p className="text-slate-400 leading-relaxed">
                  You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized use of your account.
                </p>
                <h3 className="text-lg font-medium text-white mt-4 mb-2">3.3 Account Types</h3>
                <p className="text-slate-400 leading-relaxed">
                  <strong className="text-white">Process Servers:</strong> Must provide accurate professional information and maintain any required state licenses or certifications. Subscription required for profile visibility in search results.
                </p>
                <p className="text-slate-400 leading-relaxed mt-2">
                  <strong className="text-white">Clients:</strong> May browse the directory and submit quote requests without an account.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">4. Subscriptions and Payments</h2>
                <h3 className="text-lg font-medium text-white mt-4 mb-2">4.1 Subscription Plans</h3>
                <p className="text-slate-400 leading-relaxed">
                  Process servers may subscribe to monthly or annual plans to appear in search results. Subscription fees are billed in advance and are non-refundable except as required by law.
                </p>
                <h3 className="text-lg font-medium text-white mt-4 mb-2">4.2 Price Changes</h3>
                <p className="text-slate-400 leading-relaxed">
                  Servio reserves the right to change subscription prices. Existing subscribers will be notified of any price changes at least 30 days before their next billing cycle.
                </p>
                <h3 className="text-lg font-medium text-white mt-4 mb-2">4.3 Cancellation</h3>
                <p className="text-slate-400 leading-relaxed">
                  You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. No partial refunds are provided for unused time.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">5. User Conduct</h2>
                <p className="text-slate-400 leading-relaxed">You agree not to:</p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li>Provide false or misleading information in your profile</li>
                  <li>Impersonate any person or entity</li>
                  <li>Use the Service for any illegal purpose</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Interfere with or disrupt the Service</li>
                  <li>Attempt to gain unauthorized access to any part of the Service</li>
                  <li>Use automated systems to access the Service without permission</li>
                  <li>Collect user information without consent</li>
                </ul>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">6. Verification Badges</h2>
                <p className="text-slate-400 leading-relaxed">
                  Servio offers verification badges for process servers who submit documentation of their credentials. While we make reasonable efforts to verify submitted documents, we do not guarantee the accuracy or validity of any credentials. Users should independently verify any process server's qualifications before engaging their services.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">7. Servio Score</h2>
                <p className="text-slate-400 leading-relaxed">
                  The Servio Score is a proprietary rating system based on profile completeness, verification status, response time, and other factors. The score is provided for informational purposes only and should not be the sole factor in selecting a process server. Servio reserves the right to modify the scoring algorithm at any time.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">8. Disclaimer of Warranties</h2>
                <p className="text-slate-400 leading-relaxed">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. SERVIO DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. WE DO NOT GUARANTEE THE ACCURACY, COMPLETENESS, OR USEFULNESS OF ANY INFORMATION ON THE SERVICE.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">9. Limitation of Liability</h2>
                <p className="text-slate-400 leading-relaxed">
                  SERVIO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO SERVIO IN THE TWELVE MONTHS PRECEDING THE CLAIM.
                </p>
                <p className="text-slate-400 leading-relaxed mt-4">
                  Servio is not responsible for any disputes between process servers and clients, the quality of services provided by listed process servers, or any damages resulting from services performed by process servers found through the platform.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">10. Indemnification</h2>
                <p className="text-slate-400 leading-relaxed">
                  You agree to indemnify and hold harmless Servio, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service, your violation of these Terms, or your violation of any rights of another party.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">11. Termination</h2>
                <p className="text-slate-400 leading-relaxed">
                  Servio reserves the right to suspend or terminate your account at any time for violation of these Terms or for any other reason at our sole discretion. Upon termination, your right to use the Service will immediately cease.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">12. Governing Law</h2>
                <p className="text-slate-400 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the courts located in Texas.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">13. Contact Information</h2>
                <p className="text-slate-400 leading-relaxed">
                  For questions about these Terms of Service, please visit our website for current contact information.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default TermsPage;
